// =====================================================
// File Upload Validation Utility
// AdminMunicipal v6.0 - Security Enhancement
// Validates file uploads using magic numbers (file signatures)
// to prevent MIME type spoofing attacks
// =====================================================

/**
 * Magic number signatures for common file types.
 * These are the first bytes of the file that identify its true type.
 */
const FILE_SIGNATURES: Record<string, number[][]> = {
  // Documents
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]], // %PDF

  // Images
  'image/jpeg': [[0xFF, 0xD8, 0xFF]],
  'image/png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
  'image/gif': [
    [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], // GIF87a
    [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], // GIF89a
  ],
  'image/webp': [[0x52, 0x49, 0x46, 0x46]], // RIFF (WebP uses RIFF container)
  'image/bmp': [[0x42, 0x4D]], // BM

  // Microsoft Office (old format - compound document)
  'application/msword': [[0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1]],
  'application/vnd.ms-excel': [[0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1]],
  'application/vnd.ms-powerpoint': [[0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1]],

  // Microsoft Office (new format - ZIP-based OOXML)
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [[0x50, 0x4B, 0x03, 0x04]], // DOCX
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [[0x50, 0x4B, 0x03, 0x04]], // XLSX
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [[0x50, 0x4B, 0x03, 0x04]], // PPTX

  // Archives
  'application/zip': [[0x50, 0x4B, 0x03, 0x04]],

  // Text (no signature - validated by extension only)
  'text/plain': [],
  'text/csv': [],
};

/**
 * List of dangerous file extensions that should never be allowed.
 * These could potentially execute code on the server or client.
 */
const DANGEROUS_EXTENSIONS = [
  // Executables
  '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif', '.application',
  // Scripts
  '.js', '.jse', '.vbs', '.vbe', '.ws', '.wsf', '.wsc', '.wsh',
  '.ps1', '.ps1xml', '.ps2', '.ps2xml', '.psc1', '.psc2',
  // System files
  '.dll', '.sys', '.drv', '.ocx', '.cpl',
  // Server-side scripts
  '.php', '.php3', '.php4', '.php5', '.phtml',
  '.asp', '.aspx', '.ascx', '.ashx', '.asmx', '.asa',
  '.jsp', '.jspx', '.jspa',
  '.cgi', '.pl', '.py', '.pyc', '.pyo', '.rb', '.sh', '.bash',
  // Other dangerous
  '.htaccess', '.htpasswd', '.hta', '.jar', '.war',
  '.reg', '.inf', '.scf', '.lnk', '.url',
];

/**
 * Allowed file extensions for uploads.
 * Whitelist approach is more secure than blacklist.
 */
const ALLOWED_EXTENSIONS = [
  // Documents
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.rtf', '.odt', '.ods', '.odp',
  // Images
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg',
  // Archives (only if needed)
  '.zip',
];

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates a file's magic number signature against its declared MIME type.
 * This helps detect files that have been renamed to bypass MIME type checks.
 *
 * @param file - The file to validate
 * @param expectedType - The expected MIME type
 * @returns true if signature matches or no signature is defined for type
 */
export async function validateFileSignature(file: File, expectedType: string): Promise<boolean> {
  const signatures = FILE_SIGNATURES[expectedType];

  // If no signature is defined for this type, allow it (validated by extension)
  if (!signatures || signatures.length === 0) {
    return true;
  }

  // Read the first 8 bytes of the file
  const buffer = await file.slice(0, 8).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Check if any of the known signatures match
  return signatures.some((sig) =>
    sig.every((byte, index) => bytes[index] === byte)
  );
}

/**
 * Sanitizes a filename to prevent path traversal and other attacks.
 *
 * @param filename - The original filename
 * @returns Sanitized filename
 */
export function sanitizeFilename(filename: string): string {
  // Remove path traversal attempts
  let sanitized = filename.replace(/\.\./g, '');

  // Remove dangerous characters
  sanitized = sanitized.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_');

  // Remove leading/trailing dots and spaces
  sanitized = sanitized.replace(/^[\s.]+|[\s.]+$/g, '');

  // Limit length (255 is common filesystem limit)
  sanitized = sanitized.slice(0, 255);

  // Ensure there's still a valid filename
  if (!sanitized || sanitized === '') {
    sanitized = 'unnamed_file';
  }

  return sanitized;
}

/**
 * Gets the file extension from a filename.
 *
 * @param filename - The filename
 * @returns Lowercase extension including the dot (e.g., ".pdf")
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === filename.length - 1) {
    return '';
  }
  return filename.slice(lastDot).toLowerCase();
}

/**
 * Checks if a file has a dangerous extension.
 *
 * @param filename - The filename to check
 * @returns true if the extension is dangerous
 */
export function hasDangerousExtension(filename: string): boolean {
  const ext = getFileExtension(filename);
  return DANGEROUS_EXTENSIONS.includes(ext);
}

/**
 * Checks if a file has an allowed extension.
 *
 * @param filename - The filename to check
 * @returns true if the extension is in the allowed list
 */
export function hasAllowedExtension(filename: string): boolean {
  const ext = getFileExtension(filename);
  return ALLOWED_EXTENSIONS.includes(ext);
}

/**
 * Comprehensive file validation that checks:
 * 1. File size
 * 2. Dangerous extensions (blacklist)
 * 3. Allowed extensions (whitelist)
 * 4. MIME type
 * 5. Magic number signature
 *
 * @param file - The file to validate
 * @param acceptedTypes - Array of accepted MIME types
 * @param maxSize - Maximum file size in bytes (default: 10MB)
 * @returns Validation result with error message if invalid
 */
export async function validateUploadFile(
  file: File,
  acceptedTypes: string[],
  maxSize: number = 10 * 1024 * 1024 // 10MB default
): Promise<FileValidationResult> {
  // 1. Check file size
  if (file.size > maxSize) {
    const maxMB = Math.round(maxSize / 1024 / 1024);
    return {
      valid: false,
      error: `El archivo excede el tamaño máximo de ${maxMB}MB`,
    };
  }

  // 2. Check for dangerous extensions (blacklist)
  if (hasDangerousExtension(file.name)) {
    return {
      valid: false,
      error: 'Este tipo de archivo no está permitido por razones de seguridad',
    };
  }

  // 3. Check for allowed extensions (whitelist)
  if (!hasAllowedExtension(file.name)) {
    return {
      valid: false,
      error: 'Extensión de archivo no permitida. Formatos aceptados: PDF, Word, Excel, imágenes',
    };
  }

  // 4. Check MIME type
  if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Tipo de archivo no permitido',
    };
  }

  // 5. Validate magic number signature
  try {
    const signatureValid = await validateFileSignature(file, file.type);
    if (!signatureValid) {
      return {
        valid: false,
        error: 'El contenido del archivo no coincide con su tipo declarado',
      };
    }
  } catch {
    // If we can't read the file, reject it
    return {
      valid: false,
      error: 'No se pudo verificar el contenido del archivo',
    };
  }

  return { valid: true };
}

/**
 * Formats file size for display.
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
